import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { GlobalFunctions } from '@global/GlobalFunctions';
import { ActionsDataService } from '@services/actions-data.service';
import { AssetsDataService } from '@services/assets-data.service';
import { AuthenticationService, WebAppUser } from '@services/authentication.service';
import { EventsDataService } from '@services/events-data.service';
import { RulesDataService } from '@services/rules-data.service';
import { combineLatest, takeUntil, distinctUntilChanged, filter, Subject, firstValueFrom } from 'rxjs';
import { siteSubscriptions } from '@services/siteSubscriptions';
import { Account, AccountsService, Feature } from '@services/accounts.service';

/**
 * AccountManageFeaturesComponent
 */
@Component({
	selector: 'app-account-manage-features',
	templateUrl: './account-manage-features.component.html',
	styleUrls: ['./account-manage-features.component.scss']
})
export class AccountManageFeaturesComponent implements OnInit, OnDestroy {

	/** Observable Subject Finalise */
	private finalise = new Subject<void>();

	/** Account GUID */
	@Input() accountGUID: string;

	/** organisationUID */
	public organisationUID: string;

	/** currentUser */
	public currentUser: WebAppUser;

	/** accounts */
	public myAccounts: Account[] = [];

	/** fields */
	public fields: { text: string, value: string } = { text: "Name", value: "ID" };

	/** selectedAccount */
	public selectedAccount: Account;

	/** allFeatures */
	public allFeatures: Feature[] = [];

	/** accountFeatures */
	public accountFeatures: { Feature: Feature, Has: boolean, Enabled: boolean }[] = [];

	/** featuresChanged */
	public featuresChanged: boolean = false;

	/**
	 * Default Constructor
	 * @param rulesDataService rulesDataService  
	 * @param assetsDataService assetsDataService 
	 * @param eventsDataService eventsDataService 
	 * @param authenticationService authenticationService  
	 * @param siteSubscriptions siteSubscriptions 
	 * @param actionsDataService actionsDataService 
	 * @param accountService accountService 
	 * @param globalFunctions globalFunctions  
	 */
	constructor(
		public rulesDataService: RulesDataService,
		public assetsDataService: AssetsDataService,
		public eventsDataService: EventsDataService,
		public authenticationService: AuthenticationService,
		public siteSubscriptions: siteSubscriptions,
		public actionsDataService: ActionsDataService,
		public accountService: AccountsService,
		private globalFunctions: GlobalFunctions
	) { }

	/**
	 * ngOnInit
	 */
	ngOnInit(): void {

		combineLatest([
			this.siteSubscriptions.selectedOrganisationUID$.pipe(takeUntil(this.finalise), distinctUntilChanged()),
			this.siteSubscriptions.selectedAccount$.pipe(takeUntil(this.finalise), distinctUntilChanged()),
			this.authenticationService.currentUser$.pipe(takeUntil(this.finalise), distinctUntilChanged()),
			this.accountService.getAllFeatures$().pipe(takeUntil(this.finalise), distinctUntilChanged()),
			this.siteSubscriptions.myAccounts$.pipe(takeUntil(this.finalise), distinctUntilChanged())
		]).pipe(
			// Ensure that subscriptions are not null or undefined
			filter(([selectedOrganisationUID, selectedAccount, currentUser, allFeatures, myAccounts]) =>
				!!selectedOrganisationUID && !!selectedAccount && !!currentUser && !!allFeatures && !!myAccounts)
		).subscribe({
			next: ([selectedOrganisationUID, selectedAccount, currentUser, allFeatures, myAccounts]) => {
				this.organisationUID = selectedOrganisationUID;
				this.myAccounts = myAccounts.sort((a: Account, b: Account) => a.ID - b.ID);
				this.myAccounts = this.siteSubscriptions.normalizeFeatures(this.myAccounts) as Account[];
				this.allFeatures = allFeatures;
				this.currentUser = currentUser;
				this.selectedAccount = this.myAccounts.find((a) => a.GUID === selectedAccount.GUID)!;

				//Make sure the selected account is set in the siteSubscriptions service
				this.siteSubscriptions.SetSelectedAccount(this.selectedAccount, true);

				this.UpdateScreen(this.selectedAccount);
			},
			error: (error) => {
				this.globalFunctions.HandleError(error, "Error: Error in one of the combined subscriptions", false);
			}
		});
	}

	/**
	 * Page On Destroy Event Handler
	 */
	ngOnDestroy(): void {
		try {
			// Unsubscribe from all subscriptions
			this.finalise.next();
			this.finalise.complete();
		}
		catch { }
	}

	/**
	 * selectAccount
	 * @param account account  
	 */
	selectAccount(account: Account) {
		if (this.featuresChanged == true) {
			this.globalFunctions.UserConfirm$('Lose any unsaved changes?').subscribe(OKResult => {
				if (OKResult) { this.UpdateScreen(account); }
			});
		} else { this.UpdateScreen(account); }
	}

	/**
	 * Update Screen with Account Features
	 * @param account account
	 */
	UpdateScreen(account: Account) {
		try {
			this.selectedAccount = account;
			this.accountFeatures = [];
			this.featuresChanged = false;
			if (this.allFeatures.length > 0) {
				this.accountFeatures = this.allFeatures.map((x: Feature) => ({
					Feature: x,
					Has: this.selectedAccount?.Features.some((y: any) => y.FeatureID == x.FeatureID),
					Enabled: this.selectedAccount?.Features.some((y: any) => y.FeatureID == x.FeatureID && y.Enabled),
				}))
			}
		} catch (error) { }
	}

	/**
	 * patchAccountFeatures
	 * @param account account 
	 */
	async patchAccountFeatures(account: Account) {

		let data = this.accountFeatures.map((x) => ({ FeatureID: x.Feature.FeatureID, Has: x.Has, Enabled: x.Enabled }));

		try {
			const response = await firstValueFrom(
				this.accountService.patchAccountFeatures$(account.GUID, data)
					.pipe(
						filter((result) => !!result)
					)
			);

			if (response["Error"] == "") {
				this.globalFunctions.DisplaySnackBar({
					message: "Account Features Updated",
					duration: 1000,
					action: ""
				});
			}
			else {
				this.globalFunctions.DisplaySnackBar({
					message: "Error Updating Account Features: " + response["Error"],
					duration: 5000,
					action: ""
				});
			}

		}
		catch (error: any) {
			if (error.status != 404) {
				this.globalFunctions.HandleError(error, "this.accountService.patchAccountFeatures$");
			}
		} finally {
			this.getAccounts(this.currentUser.Sub);
			this.featuresChanged = false;
		}
	}

	/**
	 * updateValue
	 * @param event event  
	 * @param accountFeature accountFeature  
	 * @param isHas isHas  
	 */
	updateValue(event: any, featureID: number, isHas: boolean) {
		let accountFeature = this.accountFeatures.find((x) => x.Feature.FeatureID == featureID);
		if (accountFeature) {
			this.featuresChanged = true;
			if (isHas) {
				accountFeature.Has = event.checked;
				if (!event.checked) {
					accountFeature.Enabled = false;
				}
			} else {
				accountFeature.Enabled = event.checked;
			}
		}
	}

	/**
	 * Get a list of accounts for this logged on user
	 * @param userPublicID
	 */
	getAccounts(userPublicID: string): void {
		let showDisabled = false

		let data = {
			"userPublicID": userPublicID,
			"showDisabled": showDisabled
		};

		this.siteSubscriptions.RefreshMyAccounts(data, true);

		//this.UpdateScreen(this.selectedAccount);

	}
}
